import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useReducer,
  useRef,
} from 'react';
import axios from 'axios';
import _find from 'lodash/find';

// utils
import api, { endpoint, setAuthorizationHeader } from 'utils/apis/axiosAPI';
// hooks
import { useAsync, useRefState } from 'utils/hooks';
import history from 'utils/history';
import useDeviceDetect from 'utils/useDeviceDetect';
import { getObjId } from 'utils/helpers/helper.general';
import { isDateBeforeToday } from 'utils/moment/moment.utils';

// capacitor
import { default as appPushNotifications } from 'appCapacitor/helpers/pushNotifications';
import { default as appHelper } from 'appCapacitor/helpers/helper.capacitor';

// global user
//import { withUser } from 'engine/Contexts/Notifications.context';

const apiURL = appHelper.getBaseApiUrl();
axios.defaults.baseURL = apiURL;

/**
 * Context for Notifications
 */
const initState = {
  _id: null,
  username: false,
  email: false,
  language: '',
  lastAction: '',
  description: false,
  address: false,
  image: false,
  role: false,
  userAuth: false,
  isTourFinished: false,
  registrationTokens: [],
  amountAbsences: 0,
  notificationsSetting: {
    pushNotification: true,
    emailNotification: true,
  },
  lastOpenedApp: '',
  childsNotifications: [],
  students: [],
  activeChild: false,
  // absence, only for parents
  activeChildHasSickness: false,
  activeChildSicknessAbs: false,
  activeChildAbsences: false,
  // teacher
  schoolsNotifications: [],
  schools: [
    {
      _id: '1',
      name: "Toute l'école",
      image: null
    }
  ],
  activeSchool: {
    _id: '1',
    name: "Toute l'école",
    image: null
  },
};
const reducer = (s, v) => ({ ...s, ...v });

const UserContext = createContext();
const UserDispatchContext = createContext();

export const useStateUser = () => {
  const user = useContext(UserContext);
  if (!user) {
    throw Error('useStateUser must be call withing UserProvider');
  }
  return user;
};

export const useDispatchUser = () => {
  const setUser = useContext(UserDispatchContext);
  if (!setUser) {
    throw Error('useDispatchUser must be call withing UserProvider');
  }
  return setUser;
};

export const UserProvider = ({ children }) => {

  let UserProviderData = {};

  const [user, setUser] = useReducer(reducer, initState);
  const [token, setToken] = useState('');
  // for active/checked child (for parents)
  const [pushNotifData, setPushNotifData] = useState(null);

  const [childChange, setChildChange] = useState(false);
  const [logOutTimer, setLogOutTimer] = useState(false);

  const { isLoading, isSuccess, run, data } = useAsync();
  const { isMobile } = useDeviceDetect();

  const publicPaths = ['/signupparent', '/reset', '/confirm', '/404'];

    // check if open page for online
  // if detail page => is on open page except community detail page
  UserProviderData.isPublicPath = useCallback(() => {
    const pathname = window.location.pathname;
    const isPath = publicPaths.some(p => pathname.includes(p));
    if (!!isPath) {
      return true;
    } else {
      return false;
    }
  }, []);

  UserProviderData.initUser = useCallback(async () => {
    const localUser = await UserProviderData.getLocalUser();
    if (!!localUser) {
      localStorage.setItem('logout', false);
      setToken(localUser?.token);
      setAuthorizationHeader(localUser?.token);
      if (!!localUser?._id) {
        run(api.users.getByID(localUser?._id));
      }
    } else {
      if (UserProviderData.isPublicPath()) {
        return false;
      } else {
        history.push('/login');
      }

    }
  }, [history, localStorage]);

  // this useEffect runs only the first render
  useEffect(() => {
    UserProviderData.initUser();
    return () => {
      logOutTimer && clearTimeout(logOutTimer);
    }
  }, []);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (isSuccess && data) {
     // console.debug('data', JSON.stringify(data))
      setUser(({ ...user, ...data }));
        // on first load of data set active child
      UserProviderData.setIniChild(data);
      UserProviderData.setIniSchool(data);
    }
  }, [data]);

  useEffect(() => {
    if (pushNotifData && Object.keys(pushNotifData).length > 0) {
      const { absenceId, childId, notifAmount } = pushNotifData;
      if (!!absenceId) {
        UserProviderData.agregateAbcenseNotification(pushNotifData);
      } else {
        if (user?.activeChild?._id == childId) {
          UserProviderData.changeChild(user?.activeChild);
        } else if (!!childId) {
          user?.students?.map(child => {
            if (child?._id == childId) {
              // console.debug('params child?._id: ', JSON.stringify(child?._id))
              UserProviderData.changeChild(child);
            }
          })
        }
      }
    }
  }, [pushNotifData, user?.activeChild?._id]);

  UserProviderData.agregateAbcenseNotification = useCallback((pushNotifData) => {
    const { absenceId, childId, absStatus, notifAmount } = pushNotifData;
    if (absenceId && childId && !absStatus) {
      if (user?.activeChild?._id == childId) {
        UserProviderData.changeChild(user?.activeChild, absenceId);
      } else {
        user?.students?.map(child => {
          if (child?._id == childId) {
            // console.debug('params child?._id: ', JSON.stringify(child?._id))
            UserProviderData.changeChild(child, absenceId);
          }
        })
      }
    } else if (absenceId && childId && absStatus) {
      if (user?.activeChild?._id == childId) {
        UserProviderData.changeChild(user?.activeChild);
      } else {
        user?.students?.map(child => {
          if (child?._id == childId) {
            // console.debug('params child?._id: ', JSON.stringify(child?._id))
            UserProviderData.changeChild(child);
          }
        })
      }
    }
  }, [pushNotifData, user?.activeChild, user?.students]);


  /**
   * User Core
   */
  UserProviderData.addToken = useCallback(token => setToken(token), []);

  UserProviderData.setAutorization = useCallback(auth => {
    setUser({ ...user, userAuth: auth ? true : false });
    auth && localStorage.setItem('logout', false);
  }, [setUser, user]);

  UserProviderData.isAuthenticated = useCallback(() => {
    const local = localStorage.getItem('schoolinkd_user');
    let localUser = false;
    if (local !== null) {
      const localJson = JSON.parse(local);
      localUser = !!localJson?.token;
    }
    return localUser;
  }, []);

  UserProviderData.populateUser = useCallback(async userData => {
    setUser({ ...user, ...userData });
    console.debug('WEB - REGISTRATION');
    //console.debug('WEB - REGISTRATION:', appHelper.isWeb, userData?.role, userData?.token);
    setAuthorizationHeader(userData?.token);
    if (userData?.role == 'parent') {
      await UserProviderData.setIniChild(userData);
    }
    if (userData?.role == 'teacher') {
      await UserProviderData.setIniSchool(userData);
    }

    if (!appHelper.isWeb) {
      console.debug('Capacitor - REGISTRATION');
      appHelper.askCameraPermissions();
      appHelper.askRecordAudioPermission();
      console.debug(JSON.stringify(appPushNotifications))
      appPushNotifications.registerPush();
    }
  }, [user, appHelper.isWeb]);

  UserProviderData.patchUser = useCallback(async userData => {
    if (!!userData?._id) {
      const userResult = await api.users.update(userData);
      if (userResult) {
        setUser(prev => ({ ...prev, ...userResult }));
      } else {
        console.error('patchUser Error');
      }
    } else {
      console.error('patchUser Error: inside userData don`t have "_id"');
    }
  }, [user]);

  /**  
   * User School
   */
  UserProviderData.setIniSchool = async (data) => { 
    const localSchool = await UserProviderData.getLocalActiveSchool();
    const userSchools = initState?.schools || [];
    if (data?.role == 'teacher' && (userSchools?.length > 0 || data?.schools > 0)) {
      setUser(prev => ({ ...prev, schools: userSchools || data?.schools }));

      if (!localSchool) {
        UserProviderData.changeSchool(userSchools[0]);
      } else {
        if (localSchool?.teacher == data?._id) {
          const schoolIndex = userSchools?.findIndex(
            sc => sc?._id === localSchool?._id,
          );
          const updatedSchool = schoolIndex !== -1 ? userSchools[schoolIndex] : userSchools[0]
          UserProviderData.changeSchool(updatedSchool);
        } else {
          UserProviderData.changeSchool(userSchools[0]);
        }
      }
    } else {
      setUser(prev => ({ ...prev, schools: [], activeChild: false }));
      await UserProviderData.getSchoolNotification();
      localStorage.setItem('schoolinked_child', JSON.stringify({}));
    }
    return userSchools;
  };

  UserProviderData.changeSchool = useCallback(async (school) => {
    setUser(prev => ({ ...prev, activeSchool: school }));
    await UserProviderData.setLocalActiveSchool(school);
    await UserProviderData.getSchoolNotification(school);
    return school;
  }, []);

  
  UserProviderData.getSchoolNotification = useCallback(async (schoolID) => {
    const localUser = await UserProviderData.getLocalUser();
    if (!!localUser?._id && localUser?.role == 'teacher') {
      setToken(localUser?.token);
      setAuthorizationHeader(localUser?.token);
      const resultComm = await api.communications.fetchNotRead(getObjId(localUser)) || false;
      if (!!resultComm) {
        setUser(prev => {
          let prevNotif = prev?.schoolsNotifications || [];
          const hasSchool = prevNotif.some(ch => getObjId(ch) == getObjId(schoolID));
          if (!!hasSchool) {
            prevNotif = prevNotif?.map(ch => {
              if (getObjId(ch) != getObjId(schoolID)) {
                return ch
              }
              return {
                ...ch,
                ...resultComm
              }
            })
          } else {
            prevNotif.push({
              _id: getObjId(schoolID),
              ...resultComm
            })
          }
          return ({
            ...prev, 
            schoolsNotifications: prevNotif
          })
        });
      } else {
        setUser(prev => ({
          ...prev, 
          schoolsNotifications: []
        }));
      }
      return resultComm;
    } else {
      setUser(prev => ({
        ...prev, 
        schoolsNotifications: []
      }));
      return false;
    }
  }, [setToken, setAuthorizationHeader]);

  /**
   * User Children
   */
  UserProviderData.setIniChild = async (data) => {
    const localChild = await UserProviderData.getLocalActiveChild();
    const parentChildren = await api.users.getParentChildren(data?._id) || data?.students;
  
    if (data?.role == 'parent' && (parentChildren?.length > 0 || data?.students > 0)) {
      setUser(prev => ({ ...prev, students: parentChildren || data?.students }));

      if (!localChild) {
        UserProviderData.changeChild(parentChildren[0]);
      } else {
        if (localChild?.parent == data?._id) {
          const childIndex = parentChildren?.findIndex(
            std => std?._id === localChild?._id,
          );
          const updatedChild = childIndex !== -1 ? parentChildren[childIndex] : parentChildren[0]
          UserProviderData.changeChild(updatedChild);
        } else {
          UserProviderData.changeChild(parentChildren[0]);
        }
      }
    } else {
      setUser(prev => ({ ...prev, students: [], activeChild: false }));
      localStorage.setItem('schoolinked_child', JSON.stringify({}));
    }
    return parentChildren;
  };

  UserProviderData.changeChild = useCallback(async (child, absId = null) => {
    setUser(prev => ({ ...prev, activeChild: child }));
    await UserProviderData.setLocalActiveChild(child);
    await UserProviderData.changeActiveChildAbsence(child, absId);
    await UserProviderData.getChildNotification(child);
    return child;
  }, []);

  UserProviderData.changeActiveChildAbsence = useCallback(async (childData, absId = null) => {
    if (!!childData?.absences) {
      let absSickness = childData?.absences?.find(abs => abs.type == 'sickness' && abs?.status == 'sick');

      if (!!absId) {
        absSickness = childData?.absences?.find(abs => abs.type == 'sickness' && abs?.status == 'sick' && abs?._id == absId);
      }
      
      setUser(prev => ({
        ...prev,
        activeChildAbsences: childData?.absences,
      }));
  
      if (absSickness) {
        switch (absSickness?.duration) {
          case 'day':
          case 'half-day':
          case 'more': {
            const isAbsCreatedBeforeNow = isDateBeforeToday(absSickness?.date);
            if (isAbsCreatedBeforeNow) {
              setUser(prev => ({
                ...prev,
                activeChildSicknessAbs: absSickness,
                activeChildHasSickness: true,
              }));
              
            }
          }
          default:
            const isAbsCreatedBeforeNow = isDateBeforeToday(absSickness?.creationDate);
            if (isAbsCreatedBeforeNow) {
              setUser(prev => ({
                ...prev,
                activeChildSicknessAbs: absSickness,
                activeChildHasSickness: true,
              }));
            }
        }
  
      }
      return absSickness;
    } else {
      return null;
    }
  }, []);

  UserProviderData.changeChildByID = useCallback(async (childID, absId = null) => {
    const localUser = await UserProviderData.getLocalUser();
   // setChildChange(true);
    if (localUser?.role == 'parent') {
      const parentChildren = await api.users.getParentChildren(localUser?._id);
      const child = parentChildren?.find(child => child?._id == childID);

      setUser({ ...user, students: parentChildren, activeChild: child });
      await UserProviderData.setLocalActiveChild(child);
      await UserProviderData.changeActiveChildAbsence(child, absId);
      await UserProviderData.getChildNotification(childID);
      return child;
    } else {
      return null;
    }
  }, [user]);

  UserProviderData.getChildrenList = useCallback(() => {
    if (user?.students && user?.role == 'parent') {
      return user?.students;
    } else {
      return [];
    }
  }, [user?.students, user?.role]);

  UserProviderData.getChildNotification = useCallback(async (childID) => {
    const localUser = await UserProviderData.getLocalUser();
    const localChild = !!childID && childID || await UserProviderData.getLocalActiveChild();
    if (!!localUser?._id && !!localChild && localUser?.role == 'parent') {
      setToken(localUser?.token);
      setAuthorizationHeader(localUser?.token);
      const resultComm = await api.communications.fetchNotRead(getObjId(localUser), getObjId(localChild)) || false;
      if (!!resultComm) {
        setUser(prev => {
          let prevNotif = prev?.childsNotifications || [];
          const hasChild = prevNotif.some(ch => getObjId(ch) == getObjId(childID));
          if (!!hasChild) {
            prevNotif = prevNotif?.map(ch => {
              if (getObjId(ch) != getObjId(childID)) {
                return ch
              }
              return {
                ...ch,
                ...resultComm
              }
            })
          } else {
            prevNotif.push({
              _id: getObjId(childID),
              ...resultComm
            })
          }
          return ({
            ...prev, 
            childsNotifications: prevNotif
          })
        });
      } else {
        setUser(prev => ({
          ...prev, 
          childsNotifications: []
        }));
      }
      return resultComm;
    } else {
      setUser(prev => ({
        ...prev, 
        childsNotifications: []
      }));
      return false;
    }
  }, [setToken, setAuthorizationHeader]);

  /* Notifications */
  UserProviderData.getInintNotifications = useCallback(async () => {
    const localUser = await UserProviderData.getLocalUser();
    if (!!localUser?._id) {
      setToken(localUser?.token);
      setAuthorizationHeader(localUser?.token);
      
      if (localUser?.role == 'parent') {
        const localChild = await UserProviderData.getLocalActiveChild();
        !!localChild && await UserProviderData.getChildNotification(localChild);
      } else if (localUser?.role == 'teacher') {
        await UserProviderData.getSchoolNotification();
      }

    }
  }, [setToken, setAuthorizationHeader]);
  

  /**
   * User Children Absences
   */
  UserProviderData.setAmountAbsence = useCallback(amount => {
    setUser({ ...user, amountAbsences: amount });
  }, [user]);
  UserProviderData.getAmountAbsence = useCallback(async () => {
    const localUser = await UserProviderData.getLocalUser();
    if (localUser?._id) {
      setToken(localUser?.token);
      setAuthorizationHeader(localUser?.token);
      api.route.fetchAll(`absence/pending`).then(result => {
        const finishedList = result?.filter(abs => {
          if (abs?.status == 'accepted' || abs?.status == 'sickness-finish' || abs?.status == 'refused') {
            return abs;
          }
        });
        const otherList = result?.filter(
          abs => !finishedList?.includes(abs),
        );
        setUser(prev => ({ ...prev, amountAbsences: otherList?.length || 0 }));
      });
    }

  }, []);

  /**
   * Local Data & Functions
   */
  UserProviderData.getLocalUser = useCallback(() => {
    const local = localStorage.getItem('schoolinked_user');
    const localData = local !== null && JSON.parse(local);
    return localData || false;
  }, []);

  UserProviderData.setLocalActiveChild = useCallback(async (child) => {
    const localUser = await UserProviderData.getLocalUser();
    !!child?._id && localStorage.setItem(
      'schoolinked_child',
      JSON.stringify({
        _id: child?._id,
        description: child?.description,
        image: null,
        parent: localUser?._id,
      }),
    );
  }, []);

  UserProviderData.getLocalActiveChild = useCallback(() => {
    const local = localStorage.getItem('schoolinked_child');
    const localData = local !== null && JSON.parse(local);
    return localData || false;
  }, []);

  UserProviderData.setLocalActiveSchool = useCallback(async (school) => {
    const localUser = await UserProviderData.getLocalUser();
    !!school?._id && localStorage.setItem(
      'schoolinked_school',
      JSON.stringify({
        _id: school?._id,
        name: school?.name,
        image: null,
        teacher: localUser?._id,
      }),
    );
  }, []);

  UserProviderData.getLocalActiveSchool = useCallback(() => {
    const local = localStorage.getItem('schoolinked_school');
    const localData = local !== null && JSON.parse(local);
    return localData || false;
  }, []);

  UserProviderData.setCookie = useCallback(() => {
    document.cookie = "for=checkingDailyModal; expires=" + date.getTime() + (24 * 60 * 60 * 1000);
  }, []);

  UserProviderData.checkCookie = useCallback(() => {
    let cookie = document.cookie.split(";");
    if (cookie[0].split("=")[1] = "checkingDailyModal" && new Date().valueOf() >= new Date(cookie[1].split("=")[1]).valueOf()) {
      UserProviderData.setCookie();
    }
  }, []);


  /**
   * Other
   */
  UserProviderData.logOut = useCallback(() => {
    localStorage.setItem('logout', true);
    localStorage.removeItem('schoolinked_user');
    localStorage.removeItem('schoolinked_child');
    localStorage.removeItem('token');
    localStorage.removeItem('schoolinked_tour');
    localStorage.removeItem('schoolinked_tour_current');

    setChildChange(false);
    setUser(initState);

    const logOutTimer = setTimeout(function () {
      //window.location.href = '/login';
      history.push('/login');
    }, 0);
    setLogOutTimer(logOutTimer);

    return true;
  }, [setLogOutTimer]);

  UserProviderData.swichRole = useCallback(() => {
    // we get the campaigns linked to the user
    let isApp = false;
    if (!appHelper.isWeb || isMobile) {
      isApp = true;
    }

    localStorage.removeItem('schoolinked_user');
    localStorage.removeItem('token');
    localStorage.removeItem('schoolinked_tour');
    localStorage.removeItem('schoolinked_tour_current');

    const logOutTimer = setTimeout(() => {
      // window.location.href = '/login';
      history.push({ pathname: `/login` });
    }, 0);
    setLogOutTimer(logOutTimer);
    return true;
  }, [isMobile, appHelper.isWeb]);


  /**
   * User Context ROOT
   */
  return (
    <UserContext.Provider value={{
      ...user,
      ...UserProviderData,
  
      // CUSTOM STATES
  
      // fech user data on init
      isLoading,
      isSuccess,
      // token
      token,
  
      childChange,
      setChildChange,
  
      pushNotifData,
      setPushNotifData,

    }}>
      <UserDispatchContext.Provider value={setUser}>
        {children}
      </UserDispatchContext.Provider>
    </UserContext.Provider>
  );
};

/**
 * User Context HOOK
 */
export const withUser = Component => props => (
  <UserContext.Consumer>
    {store => <Component user={store} {...props} />}
  </UserContext.Consumer>
);


/**
 * User Context HOOK FUNCTIONS
 */

export function useSetUserData() {
  const user = useStateUser();
  const setUserD = useDispatchUser();
  return newUserData => {
    const newUser = { ...user, ...newUserData };
    setUserD(newUser);
  };
}

export function useCheckPushNotificationParameters() {
  const user = useStateUser();
  return (params) => {
    if (Object.keys(params).length > 0) {
      user.setPushNotifData(params);
    }
    return params;
  };
}

export function useUserPatchData() {
  const user = useStateUser();
  return newUserData => {
    const userId = newUserData?._id ? newUserData?._id : user?._id;
    return !!userId && user.patchUser({
      _id: userId,
      ...newUserData
    })
  };
}

export function useActiveChildHasSickness() {
  const user = useStateUser();
  return () => user.activeChildHasSickness
}

export function useActiveChildAbsences() {
  const user = useStateUser();
  return () => user.activeChildAbsences
}


export function useChangeActiveChildHasSickness() {
  const user = useStateUser();
  return (status) => user.setActiveChildHasSickness(status);
}

export function useLogout() {
  const user = useStateUser();
 // const setUserD = useDispatchUser();
  return () => {
    return user.logOut();
  };
}

export function useActiveChildSicknessAbs() {
  const user = useStateUser();
 // const setUserD = useDispatchUser();
  return () => {
    return user?.activeChildSicknessAbs;
  };
}

export function useGetUserChildren() {
  const user = useStateUser();
  return () => {
    return user?.students || [];
  };
}

export function useGetLocalUser() {
  return () => {
    const local = localStorage.getItem('schoolinked_user');
    const localData = local !== null && JSON.parse(local);
    return localData || false;
  };
}

export function useGetLocalActiveChild() {
  return () => {
    const local = localStorage.getItem('schoolinked_child');
    const localData = local !== null && JSON.parse(local);
    return localData || false;
  };
}

export function useInitUser() {
  const user = useStateUser();
  return () => user.initUser();
}

export function useChangeChildByID() {
  const user = useStateUser();
  return (child) => user.changeChildByID(child);
}

export function useHasChildChange() {
  const user = useStateUser();
  return () => user.childChange;
}
export function useHasChildChangeAction() {
  const user = useStateUser();
  return (boolValue) => user.setChildChange(boolValue);
}

export function useGetInintNotifications() {
  const user = useStateUser();
  return async () => await user.getInintNotifications();
}

export function useChildNotificatoin() {
  const user = useStateUser();
  return () => user.childsNotifications;
}
export function useGetChildNotification() {
  const user = useStateUser();
  return async (childID) => await user.getChildNotification(childID);
}
export function useUpdateChildNotifications() {
  const user = useStateUser();
  const setUserD = useDispatchUser();
  return newChildNotif => {
    let newUserData = {};
    if (!!newChildNotif && !!newChildNotif?._id) {
      let prevNotif = user?.childsNotifications || [];
      const hasChild = prevNotif.some(ch => getObjId(ch) == getObjId(newChildNotif));
      if (!!hasChild) {
        prevNotif = prevNotif?.map(ch => {
          if (getObjId(ch) != getObjId(newChildNotif)) {
            return ch
          }
          return {
            ...ch,
            ...newChildNotif
          }
        })
      } else {
        prevNotif.push({
          ...newChildNotif
        })
      }
      newUserData = {
        ...user, 
        childsNotifications: prevNotif
      }
    }
    const newUser = { ...user, ...newUserData };
    setUserD(newUser);
  };
}


/* Teacher */

export function useGetUserSchools() {
  const user = useStateUser();
  return () => {
    return user?.schools || [];
  };
}

export function useGetLocalActiveSchool() {
  return () => {
    const local = localStorage.getItem('schoolinked_school');
    const localData = local !== null && JSON.parse(local);
    return localData || false;
  };
}

export function useSchoolNotificatoin() {
  const user = useStateUser();
  return () => user.schoolsNotifications;
}
export function useGetSchoolNotification() {
  const user = useStateUser();
  return async (schoolID) => await user.getSchoolNotification(schoolID);
}

export function useUpdateSchoolNotifications() {
  const user = useStateUser();
  const setUserD = useDispatchUser();
  return newSchoolNotif => {
    let newUserData = {};
    if (!!newSchoolNotif && !!newSchoolNotif?._id) {
      let prevNotif = user?.schoolsNotifications || [];
      const hasChild = prevNotif.some(ch => getObjId(ch) == getObjId(newSchoolNotif));
      if (!!hasChild) {
        prevNotif = prevNotif?.map(ch => {
          if (getObjId(ch) != getObjId(newSchoolNotif)) {
            return ch
          }
          return {
            ...ch,
            ...newSchoolNotif
          }
        })
      } else {
        prevNotif.push({
          ...newSchoolNotif
        })
      }
      newUserData = {
        ...user, 
        schoolsNotifications: prevNotif
      }
    }
    const newUser = { ...user, ...newUserData };
    setUserD(newUser);
  };
}